/**
 * globalState.js
 *
 * A drop-in replacement for the `reactn` library's global state API
 * (`useGlobal`, `withGlobal`, `setGlobal`).
 *
 * Built on React 18's `useSyncExternalStore` so it is safe for use inside
 * the concurrent renderer – components always see a consistent snapshot of
 * the store and re-render correctly whenever state changes.
 *
 * API surface intentionally mirrors `reactn` v2 so that all call-sites can
 * be updated by changing only the import path.
 */

import React, { useSyncExternalStore, useCallback } from "react";

// ─── Internal store ───────────────────────────────────────────────────────────

let _state = {};
const _listeners = new Set();

function _subscribe(listener) {
  _listeners.add(listener);
  return () => _listeners.delete(listener);
}

// Always returns the same reference when state hasn't changed, so
// useSyncExternalStore can bail out of re-renders correctly.
function _getSnapshot() {
  return _state;
}

// ─── Public API ───────────────────────────────────────────────────────────────

/**
 * Merge `updates` into the global state and notify all subscribers.
 * Safe to call before any component mounts (mirrors reactn behaviour).
 *
 * @param {object} updates
 */
export function setGlobal(updates) {
  if (!updates || typeof updates !== "object") return;
  _state = { ..._state, ...updates };
  _listeners.forEach((fn) => fn());
}

/**
 * Hook – read and write a single key in the global state.
 * Returns `[value, setter]` just like reactn's `useGlobal`.
 *
 * @param {string} key
 * @returns {[any, (value: any) => void]}
 */
export function useGlobal(key) {
  const state = useSyncExternalStore(_subscribe, _getSnapshot);
  const setter = useCallback(
    (value) => {
      setGlobal({ [key]: value });
    },
    [key]
  );
  return [state[key], setter];
}

/**
 * HOC – inject mapped global-state values as props.
 * Mirrors reactn's `withGlobal`.
 *
 * @param {(globalState: object) => object} mapper
 * @returns {(WrappedComponent: React.ComponentType) => React.ComponentType}
 */
export function withGlobal(mapper) {
  return function (WrappedComponent) {
    function WithGlobalComponent(props) {
      const state = useSyncExternalStore(_subscribe, _getSnapshot);
      const mappedProps = mapper(state);
      return React.createElement(WrappedComponent, {
        ...mappedProps,
        ...props,
      });
    }
    WithGlobalComponent.displayName = `withGlobal(${
      WrappedComponent.displayName || WrappedComponent.name || "Component"
    })`;
    return WithGlobalComponent;
  };
}
